import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * useDebounceFunction
 *
 * @param {Function} fn              The function to debounce
 * @param {number}   wait            Delay in ms
 * @param {Object}   options         { leading, trailing, maxWait }
 * @param {boolean}  options.leading  Invoke on the leading edge
 * @param {boolean}  options.trailing Invoke on the trailing edge
 * @param {number}   options.maxWait  Maximum time between invocations
 *
 * @returns {Function & { cancel: Function, flush: Function, pending: Function }}
 */
export function useDebounceFunction(
  fn,
  wait,
  { leading = false, trailing = true, maxWait } = {}
) {
  const timer = useRef(null);
  const lastCallTime = useRef(0);
  const lastInvokeTime = useRef(0);
  const argsRef = useRef();

  // Always keep the latest fn
  useEffect(() => {
    argsRef.current = argsRef.current; // no-op to satisfy React Hook lint
  }, [fn]);

  // Helper to actually invoke fn
  const invoke = useCallback(() => {
    lastInvokeTime.current = Date.now();
    fn(...argsRef.current);
  }, [fn]);

  const debounced = useCallback((...args) => {
    const now = Date.now();
    argsRef.current = args;
    lastCallTime.current = now;

    // Leading-edge: call immediately if desired and no timer is set
    const callNow = leading && !timer.current;
    if (callNow) {
      invoke();
    }

    // Clear any existing timer
    if (timer.current) {
      clearTimeout(timer.current);
    }

    // Check if maxWait has elapsed
    const timeSinceLastInvoke = now - lastInvokeTime.current;
    const useDelay = (maxWait != null && timeSinceLastInvoke >= maxWait) 
      ? 0 
      : wait;

    timer.current = setTimeout(() => {
      const timeSinceLastCall = Date.now() - lastCallTime.current;

      // If calls keep coming in, reschedule
      if (timeSinceLastCall < wait && timeSinceLastCall > 0) {
        timer.current = setTimeout(() => debounced(...args), wait - timeSinceLastCall);
      } else {
        // Trailing-edge: invoke if desired
        if (trailing) {
          invoke();
        }
        timer.current = null;
      }
    }, useDelay);
  }, [invoke, leading, trailing, maxWait, wait]);

  // Cancel any pending invocation
  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // Immediately invoke if a call is pending
  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      invoke();
      timer.current = null;
    }
  }, [invoke]);

  // Check if there is a pending invocation
  const pending = useCallback(() => !!timer.current, []);

  // Auto‑cleanup on unmount
  useEffect(() => cancel, [cancel]);

  // Expose extra methods on the function
  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.pending = pending;

  return debounced;
}

/**
 * useDebounceValue
 *
 * A simple wrapper for debouncing a state value.
 *
 * @param {*} value     Any value you want debounced
 * @param {number} delay Delay in ms
 * @returns {*}         The debounced value
 */
export function useDebounceValue(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  const update = useDebounceFunction(
    (val) => setDebouncedValue(val),
    delay,
    { leading: false, trailing: true }
  );

  useEffect(() => {
    update(value);
    return () => update.cancel();
  }, [value, delay, update]);

  return debouncedValue;
}
